import React, { useState } from "react";
import { useSupportBotDocs } from "./store";
import { EmptyState, PageHeader, primaryBtn, StatusBadge } from "./shared";
import UploadModal from "./upload-modal";

// Support Bot > My Submissions — everything the signed-in user uploaded and
// where it is in review. "Changes requested" shows the admin's comment and
// lets them upload a revised file, which goes straight back to Approvals.
const MySubmissionsPage: React.FC = () => {
  const docs = useSupportBotDocs().filter((d) => d.mine);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviseOpen, setReviseOpen] = useState(false);
  const selected = docs.find((d) => d.id === selectedId) || docs[0] || null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-6">
        <PageHeader title="My Submissions" subtitle="Documents you've uploaded and their review status." />

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 grid grid-cols-1 md:grid-cols-[340px_1fr] min-h-[480px] overflow-hidden">
          <div className="border-b md:border-b-0 md:border-r border-gray-100 dark:border-gray-800">
            {docs.length === 0 ? (
              <EmptyState text="You haven't submitted any documents yet." />
            ) : (
              docs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedId(d.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 border-l-2 transition-colors ${
                    selected?.id === d.id ? "border-l-indigo-600 bg-indigo-50/60 dark:bg-indigo-500/10" : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{d.name}</p>
                  <div className="mt-1">
                    <StatusBadge status={d.status} approvedLabel />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{d.updated}</p>
                </button>
              ))
            )}
          </div>

          <div className="p-5 sm:p-6">
            {selected && (
              <>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{selected.name}</h2>
                <div className="mt-1.5">
                  <StatusBadge status={selected.status} approvedLabel />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                  {selected.product} · {selected.type} · Visible to {selected.visibleTo.join(", ")}
                </p>

                {selected.adminComment && (selected.status === "changes_requested" || selected.status === "rejected") && (
                  <>
                    <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase mt-5 mb-2">Admin's comment</p>
                    <p
                      className={`rounded-xl px-4 py-3 text-sm ${
                        selected.status === "rejected" ? "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300" : "bg-orange-50 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300"
                      }`}
                    >
                      {selected.adminComment}
                    </p>
                  </>
                )}

                {selected.status === "changes_requested" && (
                  <button type="button" onClick={() => setReviseOpen(true)} className={`${primaryBtn} mt-4`}>
                    Upload revised file
                  </button>
                )}
                {selected.status === "pending" && <p className="text-sm text-gray-500 dark:text-gray-400 mt-5">Waiting for a CRM Admin to review it.</p>}
                {selected.status === "live" && <p className="text-sm text-gray-500 dark:text-gray-400 mt-5">Approved — the bot will use this after testing.</p>}
              </>
            )}
          </div>
        </div>
      </div>
      {reviseOpen && selected && <UploadModal revise={selected} onClose={() => setReviseOpen(false)} />}
    </div>
  );
};

export default MySubmissionsPage;
